import React from 'react';
import type { EventTravel, SetPlayerTravelRequest } from '../../types/travel';
import { PlayerTravelMode, formatTravelTime, playerTravelMode } from '../../types/travel';

interface PlayerTravelChoiceProps {
  travel: EventTravel;
  playerId: number;
  busy: boolean;
  onSet: (request: SetPlayerTravelRequest) => void;
  /** "Tudok vinni valakit": a saját autóba ül (ha még nincs autó, a felajánlás nyílik meg). */
  onChooseCar: () => void;
  /** Az autó felajánlása épp nyitva van ennek a játékosnak (még nincs mentve). */
  carPending?: boolean;
  /** A nyitott felajánlás elvetése. */
  onCancelCar?: () => void;
}

// Mobilon 16px, különben az iOS ránagyít a mezőre
const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const seatedIn = (travel: EventTravel, vehicleId: number) =>
  travel.passengers.filter((p) => !p.goesDirectly && p.travelVehicleId === vehicleId).length;

/** A három válasz, ahogy a szülő látja. */
export enum TravelAnswer {
  None = 'none',
  NeedsRide = 'needsRide',
  Direct = 'direct',
  OwnCar = 'ownCar',
}

/** Melyik válasz igaz most a játékosra: a saját autóm külön válasz, minden más jármű "fuvar". */
export const travelAnswer = (travel: EventTravel, playerId: number): TravelAnswer => {
  const mode = playerTravelMode(travel, playerId);
  if (mode === PlayerTravelMode.Direct) return TravelAnswer.Direct;
  if (mode === PlayerTravelMode.NeedsRide) return TravelAnswer.NeedsRide;
  if (mode === PlayerTravelMode.Vehicle) {
    const passenger = travel.passengers.find((p) => p.playerId === playerId);
    const vehicle = travel.vehicles.find((v) => v.id === passenger?.travelVehicleId);
    return vehicle?.isMine ? TravelAnswer.OwnCar : TravelAnswer.NeedsRide;
  }
  return TravelAnswer.None;
};

/** Hogyan utazik most a játékos, egy mondatban (vagy null, ha nincs jelzés). */
export const describePlayerTravel = (travel: EventTravel, playerId: number): string | null => {
  const p = travel.passengers.find((x) => x.playerId === playerId);
  if (!p) return null;
  if (p.goesDirectly) return 'Egyenesen a helyszínre megy.';
  const vehicle = travel.vehicles.find((v) => v.id === p.travelVehicleId);
  if (!vehicle) return 'Fuvarra vár - amint beosztják, értesítést kapsz.';
  const stop = vehicle.stops.find((s) => s.id === p.travelStopId);
  const carrier = vehicle.isMine ? 'Te viszed' : vehicle.isOffered && vehicle.ownerName ? `${vehicle.ownerName} viszi` : vehicle.name;
  return `${carrier}${stop ? `, ${stop.name}` : ''}${stop?.meetTime ? `, ${formatTravelTime(stop.meetTime)}-ra` : ''}`;
};

/**
 * Egy játékos utazása: fuvar kell (és ha már van hova, hol száll be), vagy
 * saját maga megy - ilyenkor jelezhető, hogy "Tudok vinni valakit", és az
 * autó felajánlható másoknak is. Az edző a beosztást felülírhatja.
 */
const PlayerTravelChoice: React.FC<PlayerTravelChoiceProps> = ({
  travel,
  playerId,
  busy,
  onSet,
  onChooseCar,
  carPending = false,
  onCancelCar,
}) => {
  const answer = travelAnswer(travel, playerId);
  const passenger = travel.passengers.find((p) => p.playerId === playerId);
  const status = describePlayerTravel(travel, playerId);

  // Hova szállhat be: a stáb járművei (megállónként) és a többi szülő felajánlott autója
  const options = travel.vehicles
    .filter((v) => !v.isMine)
    .flatMap((v) => {
      const free = v.seats - seatedIn(travel, v.id);
      const full = free <= 0 && passenger?.travelVehicleId !== v.id;
      const suffix = full ? ' – tele' : ` – ${free} szabad hely`;
      const name = v.isOffered ? `${v.ownerName ?? 'Egy szülő'} autója` : v.name;
      if (v.stops.length === 0 || v.isOffered) {
        const stop = v.stops[0];
        const where = stop ? ` · ${stop.name}${stop.meetTime ? ` · ${formatTravelTime(stop.meetTime)}` : ''}` : '';
        return [{ value: `${v.id}|`, label: `${name}${where}${suffix}`, disabled: full }];
      }
      return v.stops.map((s) => ({
        value: `${v.id}|${s.id}`,
        label: `${name} · ${s.name}${s.meetTime ? ` · ${formatTravelTime(s.meetTime)}` : ''}${suffix}`,
        disabled: full,
      }));
    });

  const boardValue =
    answer === TravelAnswer.NeedsRide && passenger?.travelVehicleId
      ? `${passenger.travelVehicleId}|${
          travel.vehicles.find((v) => v.id === passenger.travelVehicleId)?.isOffered ? '' : passenger.travelStopId ?? ''
        }`
      : '';

  const answerButton = (active: boolean, label: string, onClick: () => void) => {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy || active}
        aria-pressed={active}
        className={`rounded-xl px-2 py-2.5 text-sm font-semibold leading-tight transition-colors disabled:cursor-default ${
          active ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {answerButton(answer === TravelAnswer.NeedsRide, 'Fuvar kell', () => onSet({ mode: PlayerTravelMode.NeedsRide }))}
        {answerButton(
          answer === TravelAnswer.Direct || answer === TravelAnswer.OwnCar,
          'Saját maga megy',
          () => onSet({ mode: PlayerTravelMode.Direct })
        )}
      </div>

      {/* Aki maga viszi a gyereket, felajánlhatja, hogy másokat is visz */}
      {(answer === TravelAnswer.Direct || answer === TravelAnswer.OwnCar) && (
        <label className="mt-2 flex cursor-pointer items-center gap-2 rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={answer === TravelAnswer.OwnCar || carPending}
            disabled={busy}
            onChange={(e) => {
              if (e.target.checked) onChooseCar();
              else if (carPending) onCancelCar?.();
              else onSet({ mode: PlayerTravelMode.Direct });
            }}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="font-medium">Tudok vinni valakit</span>
        </label>
      )}

      {answer === TravelAnswer.NeedsRide &&
        (options.length > 0 ? (
          <div className="mt-2">
            <label htmlFor={`board-${playerId}`} className="mb-1 block text-xs font-medium text-gray-600">
              Hol száll be?
            </label>
            <select
              id={`board-${playerId}`}
              value={boardValue}
              disabled={busy}
              onChange={(e) => {
                const [vehicleId, stopId] = e.target.value.split('|');
                onSet(
                  vehicleId
                    ? { mode: PlayerTravelMode.Vehicle, travelVehicleId: Number(vehicleId), travelStopId: stopId ? Number(stopId) : null }
                    : { mode: PlayerTravelMode.NeedsRide }
                );
              }}
              className={inputClass}
            >
              <option value="">Még nem tudom - az edző osszon be</option>
              {options.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled && o.value !== boardValue}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            Még nincs megszervezve, ki melyik járművel megy. Amint az edző beosztja, értesítést kapsz.
          </p>
        ))}

      {status && answer !== TravelAnswer.None && <p className="mt-2 text-sm text-primary-800">{status}</p>}
    </div>
  );
};

export default PlayerTravelChoice;
